import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiUserPlus, FiTrash2, FiShield, FiX } from 'react-icons/fi';
import { getPlatformAdmins, createPlatformAdmin, deletePlatformAdmin } from '../../services/api';
import styles from './SuperAdminPage.module.css';

const PlatformAdminsPage = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [formLoading, setFormLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const res = await getPlatformAdmins();
      setAdmins(res.data);
    } catch {
      toast.error('Failed to load platform admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAdmins(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await createPlatformAdmin(form);
      toast.success('Platform admin created');
      setShowModal(false);
      setForm({ name: '', email: '', phone: '', password: '' });
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Failed to create admin');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deletePlatformAdmin(deleteId);
      toast.success('Platform admin removed');
      setDeleteId(null);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove admin');
    }
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h2>Platform Admins</h2>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem', marginTop: 4 }}>
            Admins have full platform access but cannot create other admins
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <FiUserPlus size={14} style={{ marginRight: 6 }} /> Create Admin
        </button>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : admins.length === 0 ? (
        <div className="empty-state">
          <div className="icon"><FiShield size={32} /></div>
          <p>No platform admins yet</p>
        </div>
      ) : (
        <div className={styles.adminTable}>
          <div className={styles.adminTableHead}>
            <span>Name</span>
            <span>Email</span>
            <span>Phone</span>
            <span>Created</span>
            <span></span>
          </div>
          {admins.map((a) => (
            <div key={a.id} className={styles.adminTableRow}>
              <span style={{ fontWeight: 600 }}>{a.name}</span>
              <span style={{ color: 'var(--color-text-secondary)' }}>{a.email}</span>
              <span style={{ color: 'var(--color-text-secondary)' }}>{a.phone}</span>
              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.82rem' }}>
                {new Date(a.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
              <span style={{ textAlign: 'right' }}>
                <button className="btn btn-danger btn-sm" onClick={() => setDeleteId(a.id)}>
                  <FiTrash2 size={12} />
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Create admin modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ margin: 0 }}>Create Platform Admin</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(false)}>
                <FiX size={16} />
              </button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Amit Shah"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="admin@example.com"
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="10-digit mobile number"
                  required
                />
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="Min 6 characters"
                  required
                  minLength={6}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                  {formLoading ? 'Creating…' : 'Create Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteId && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Remove Platform Admin?</h3>
            <p style={{ color: 'var(--color-text-secondary)', marginTop: 12, fontSize: '0.9rem' }}>
              This will revoke their platform access. This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}>Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformAdminsPage;
